import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TeamMember } from '../../../interfaces/team/member/member.interface';
import { TeamInterface } from '../../../interfaces/team/team.interface';
import {
  TeamMemberProductEligibilityContext,
  TeamsService,
  UserTeamMembership,
} from '../../../services/teams/teams.service';

@Component({
    selector: 'app-member-product-eligibility-dialog',
    templateUrl: './member-product-eligibility-dialog.component.html',
    styleUrls: ['./member-product-eligibility-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MemberProductEligibilityDialogComponent implements OnInit {
  public context?: TeamMemberProductEligibilityContext;
  public loading = true;
  public saving = false;
  public error = '';
  public memberships: UserTeamMembership[] = [];
  public companies: Array<{id: string; name: string}> = [];
  public companyId = '';
  public teamId = '';
  private contextRequest = 0;
  public readonly form = this.formBuilder.group({
    productIds: [[], Validators.required],
    startDate: [''],
    endDate: [''],
  }, { validators: this.dateRangeValidator });

  constructor(
    private formBuilder: UntypedFormBuilder,
    private teamsService: TeamsService,
    public dialogRef: MatDialogRef<MemberProductEligibilityDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { team?: TeamInterface; member?: TeamMember; user?: {id: string; firstName?: string; lastName?: string; email: string} },
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.data.user) {
      this.form.get('startDate')!.setValidators(Validators.required);
      this.form.get('endDate')!.setValidators(Validators.required);
      this.form.get('startDate')!.updateValueAndValidity();
      this.form.get('endDate')!.updateValueAndValidity();
      try {
        this.memberships = (await this.teamsService.getUserTeamMemberships(this.data.user.id))
          .filter(item => !!item.companyId);
        this.companies = Array.from(new Map(this.memberships.map(item => [item.companyId!, {
          id: item.companyId!, name: item.companyName!,
        }])).values());
        if (!this.companies.length) {
          this.error = 'This user needs a team membership associated with a company before eligibility can be set.';
        } else if (this.companies.length === 1) {
          await this.selectCompany(this.companies[0].id);
        }
      } catch (error: any) {
        this.error = this.getErrorMessage(error, 'Unable to load company memberships.');
      } finally {
        this.loading = false;
      }
      return;
    }
    try {
      this.context = await this.teamsService.getTeamMemberProductEligibilityContext(
        this.data.team!.id,
        this.memberId,
      );
    } catch (error: any) {
      this.error = this.getErrorMessage(error, 'Unable to load eligible products.');
    } finally {
      this.loading = false;
    }
  }

  public get companyTeams(): UserTeamMembership[] {
    return this.memberships.filter(item => item.companyId === this.companyId);
  }

  public async selectCompany(companyId: string): Promise<void> {
    this.companyId = companyId;
    this.teamId = '';
    this.context = undefined;
    this.error = '';
    this.contextRequest++;
    this.loading = false;
    this.form.get('productIds')!.setValue([]);
    if (this.companyTeams.length === 1) {
      await this.selectTeam(this.companyTeams[0].teamId);
    }
  }

  public async selectTeam(teamId: string): Promise<void> {
    const request = ++this.contextRequest;
    this.teamId = teamId;
    this.context = undefined;
    this.error = '';
    this.form.get('productIds')!.setValue([]);
    if (!this.companyTeams.some(item => item.teamId === teamId)) { return; }
    this.loading = true;
    try {
      const context = await this.teamsService.getUserProductEligibilityContext(teamId, this.data.user!.id, this.companyId);
      if (request !== this.contextRequest) { return; }
      this.context = context;
      if (!context.products.length) { this.error = 'No products are enabled for this team.'; }
    } catch (error: any) {
      if (request === this.contextRequest) {
        this.error = this.getErrorMessage(error, 'Unable to load eligible products.');
      }
    } finally {
      if (request === this.contextRequest) { this.loading = false; }
    }
  }

  public get memberName(): string {
    if (this.data.user) {
      return [this.data.user.firstName, this.data.user.lastName].filter(Boolean).join(' ') || this.data.user.email;
    }
    const firstName = this.data.member!.firstName || this.data.member!.teamuserMetadata?.firstName || '';
    const lastName = this.data.member!.lastName || this.data.member!.teamuserMetadata?.lastName || '';
    return `${firstName} ${lastName}`.trim() || this.data.member!.email || this.data.member!.userEmail;
  }

  public getProductId(product: any): string {
    return `${product?.id || product?.vendorProductId || ''}`;
  }

  public getProductLabel(product: any): string {
    return [product?.vendorName, product?.productName].filter(Boolean).join(' ') || this.getProductId(product);
  }

  public async save(): Promise<void> {
    if (this.form.invalid || this.saving || !this.context) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = '';
    const value = this.form.value;
    try {
      const request = {
        productIds: value.productIds,
        startDate: value.startDate || null,
        endDate: value.endDate || null,
      };
      const result = this.data.user
        ? await this.teamsService.saveUserProductEligibility(this.teamId, this.data.user.id, this.companyId, request)
        : await this.teamsService.saveTeamMemberProductEligibility(this.data.team!.id, this.memberId, request);
      this.dialogRef.close(result);
    } catch (error: any) {
      this.error = this.getErrorMessage(error, 'Unable to save product eligibility.');
    } finally {
      this.saving = false;
    }
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  private get memberId(): string {
    return `${this.data.member!.memberId || this.data.member!.id || ''}`;
  }

  private getErrorMessage(error: any, defaultMessage: string): string {
    const message = error?.error?.error || error?.message || defaultMessage;
    const teamName = this.data.team?.name;
    return teamName
      ? message.replace(`Team ${this.data.team!.id}`, `Team ${teamName}`)
      : message;
  }

  private dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const startDate = control.get('startDate')?.value;
    const endDate = control.get('endDate')?.value;
    return startDate && endDate && startDate > endDate ? { invalidDateRange: true } : null;
  }
}
